import { Typography } from "@packages/ds";
import styled, { css as styledCSS } from "styled-components";

import DialogGroup from "ds/Dialog";
import * as DropdownMenu from "ds/Dropdown";
import { breakpoints, semiBoldAlias } from "Libs/theme";

// weird calculation that came from the theme in a whole object
// but only used in this file and only this value was used
const legacyGrid = "calc((100vw - 80px + 32px) / 12 - 32px)";

export const FormWrapper = styled.form`
  margin-bottom: 16px;
  padding: 0 32px 32px;
  color: var(--section-ebony-light-color, var(--ebony-light));
  background-color: var(--section-snow-background-color, var(--snow));
`;

export const Row = styled.div`
  margin: 0 -32px;
  padding: 0 32px;
  border-bottom: 1px solid
    var(--section-color-primary-100-border-color, var(--color-primary-100));
  .dropdown {
    .input-wrapper,
    .select-box {
      width: 150px;
    }
    .select-box__control,
    .select-box:hover .select-box__control {
      background: transparent;
      border: none;
    }
    .select-box__control--is-focused {
      margin: 0 -11px 0 0;
      padding-right: 11px;
      caret-color: transparent;
      box-shadow: none;
    }
    .select-box__menu {
      z-index: 20;
    }
    .select-box__single-value {
      width: 100%;
      text-align: right;
    }
    .select-box__value-container {
      padding: 0;
    }
    .select-box__dropdown-indicator {
      padding: 0;
      margin-right: -4px;
    }
  }

  &.info-icon {
    svg path {
      fill: var(--mode-vector-neutral-default);
    }
    &:hover svg path {
      fill: var(--icon-slate-fill, var(--slate));
    }
  }

  @media (max-width: ${breakpoints[1]}) {
    padding: 20px 16px 16px;
    &:after {
      content: "";
      display: block;
      clear: both;
    }
  }
`;

export const TotalRow = styled(Row)`
  border-bottom: none;
  margin-bottom: 64px;

  .info-dialog {
    cursor: default;
  }
  @media (max-width: ${breakpoints[1]}) {
    & {
      margin-bottom: 0;
    }
  }
`;

export const TotalRowDescription = styled(Typography.BodyText)`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: ${breakpoints[1]}) {
    & {
      font-size: 13px;
    }
  }
`;

export const Info = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: start;
  margin: 12px 0;
`;

export const DescriptionLabel = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  flex: 0 0 calc(${legacyGrid} * 3 + 32px);
  margin-right: 32px;
  margin-top: 9px;
`;

export const DescriptionLabelPlan = styled(DescriptionLabel)`
  margin-top: 7px;
  position: relative;
`;

export const Price = styled.div`
  display: flex;
  max-width: 45%;
  word-break: break-word;
  margin-top: 9px;
`;

export const Options = styled.div`
  display: flex;
  align-items: center;
  order: 1;
  margin-left: auto;
  @media (max-width: ${breakpoints[1]}) {
    & {
      float: right;
    }
  }
`;

export const PlanOption = styled(Options)`
  .dropdown {
    .input-wrapper,
    .select-box {
      width: 180px;
    }
  }
`;

export const GeneralOption = styled(Options)`
  .dropdown {
    .input-wrapper,
    .select-box {
      width: 80px;
    }
  }
`;
export const AppSizeOption = styled(Options)`
  .dropdown {
    .input-wrapper,
    .select-box {
      width: 120px;
    }
  }
`;

export const BackupsOption = styled(Options)`
  ${DropdownMenu.Trigger} {
    padding: 0px;
  }
`;

export const Total = styled.div<{
  $extraMargin?: boolean;
  $small?: boolean;
}>`
  height: 40px;
  min-width: 80px;
  box-sizing: border-box;
  line-height: 40px;
  padding: 0 12px;
  font-size: ${({ $small }) => ($small ? "12px" : "15px")};
  font-weight: 600;
  background-color: var(--input-ice-background-color, var(--ice));
  color: var(--header-night-color, var(--night));
  margin-left: ${({ $extraMargin }) => ($extraMargin ? "30px" : "16px")};
`;

export const MainLabelWrapper = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  flex: 0 0 calc(${legacyGrid} * 3 + 32px);
  margin-right: 32px;
  align-self: start;
  margin-top: 9px;

  small {
    font-size: 15px;
    font-weight: 400;
    color: var(--mode-text-neutral-default-weak);
  }
`;

export const DialogGroupDefault = styled(DialogGroup)`
  &.info-dialog {
    position: absolute;
    right: 0;
    top: 0;
  }
`;

export const IntegrationActionLinkWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

export const UserLicenses = styled.div`
  margin-left: 10px;
`;

export const SubscriptionStorage = styled.div`
  margin: 0 10px 0 14px;
`;

export const Heading = styled.div`
  display: flex;
  width: 100%;
  align-items: start;
  min-height: 64px;

  ${DescriptionLabelPlan} > button {
    position: absolute;
  }
  .info-dialog {
    vertical-align: middle;
  }

  ${Price} + button {
    margin-top: 8px;
  }

  .included {
    font-size: 12px;
    font-weight: 600;
  }

  @media (max-width: ${breakpoints[1]}) {
    display: inline;
    line-height: 24px;
    ${DescriptionLabel},
    ${Info}, .price {
      display: flex;
      flex-wrap: wrap-reverse;
      width: auto;
      align-items: center;
    }
    ${DescriptionLabel} {
      padding: 8px 0 0;
      font-size: 15px;
      line-height: 24px;
      margin-top: 0;
    }
    ${MainLabelWrapper} {
      font-size: 15px;
      line-height: 24px;
      margin-top: 0;
      margin-right: 0px;
      display: inline-grid;
      max-width: 117px;
    }
    ${Info} {
      margin: 0;
    }
    ${Price} {
      font-size: 14px;
      padding-right: 77px;
      position: relative;
      margin-top: 8px;
      align-items: center;
      margin-top: 0;
    }
  }
`;

export const TotalHeading = styled(Heading)`
  padding-top: 24px;
  padding-bottom: 0;
  min-height: auto;
`;

export const Features = styled.div`
  display: flex;
  align-items: flex-start;
`;

export const Includes = styled.div<{ plan?: string }>`
  ${DescriptionLabel} {
    font-size: 15px;
    margin-bottom: 16px;
    ${semiBoldAlias()};
    @media (max-width: ${breakpoints[1]}) {
      padding-top: 19px;
    }
  }
  ${({ plan }) =>
    plan === "development" &&
    `
    & .included ul {
    column-count: 2;
    width: 800px;
  }
  `}
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  min-height: 32px;
  box-sizing: border-box;
  margin-top: 32px;
  button + button {
    margin-left: 16px;
  }
`;

export const LinkContainer = styled.div`
  color: var(--link-color-primary-600-color, var(--color-primary-600));
`;

export const Label = styled(Typography.Label)`
  font-style: ${semiBoldAlias()};
  font-size: 14px;
  color: var(--menu-ebony-light-color, var(--ebony-light));
`;

export const ContentPlan = styled(DropdownMenu.Content)`
  width: 180px;
  min-width: unset;
  height: 300px;
  overflow-y: scroll;
`;
export const ContentStorage = styled(ContentPlan)`
  width: 80px;
  height: 320px;
`;
export const ContentEnvs = styled(DropdownMenu.Content)`
  width: 80px;
  min-width: unset;
`;
export const ContentBigDev = styled(ContentEnvs)`
  width: 120px;
`;

export const ContentBackup = styled(ContentEnvs)`
  width: 356px;
`;

export const Item = styled(DropdownMenu.Item)`
  height: max-content;
  white-space: break-spaces;
  & p {
    color: var(--menu-ebony-light-color, var(--ebony-light));
  }

  ${({ $isSelected }) =>
    $isSelected &&
    styledCSS`
      & ${Label} {
        color: var(--menu-color-primary-600-color,var(--color-primary-600));
      }
    `}
`;

export const ValueText = styled(Typography.BodyText)`
  color: var(--mode-text-neutral-default-weak);
`;

export const TriggerChevron = styled.div`
  display: flex;
  align-items: center;
  color: var(--icon-granite-color, var(--granite));

  ${DropdownMenu.Chevron} {
    margin-left: 0px;
    margin-right: -8px;
    width: 28px;
    height: 28px;
  }
`;

export const TriggerWrapper = styled.div`
  display: flex;
  align-items: center;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;

  &:hover {
    color: var(--icon-color-primary-600-color, var(--color-primary-600));

    & ${TriggerChevron} {
      color: var(--icon-color-primary-600-color, var(--color-primary-600));
    }
  }
`;

export const BlackFireModalPrompt = styled.div`
  .blackfire-prompt_title {
    font-size: 18px;
    line-height: 24px;
  }
  .blackfire-prompt_notice {
    display: block;
    margin-top: 16px;
    font-weight: 600;
    font-size: 13px;
    line-height: 20px;
    a {
      padding: 0;
      margin: 0;
    }
  }
`;

export const Note = styled.strong`
  font-weight: 700;
`;
